import React, {FC, useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {Button, Modal, Spin, Table} from 'antd';

import { enquirySummary } from '../../service/SummaryService';
import EventDetailsDialog from '../EventDetailsDialog/EventDetailsDialog';

import {EnquirySummary, EventSummary} from "../../model/EnquirySummary";

import './EnquirySummaryDialog.less';
import LinkButton from "../../component/LinkButton";

//
// Display an enquiry summary
//

const EnquirySummaryDialog: FC<{
  onExit: () => void,
  open: boolean,
  enquiryId: string | undefined,
  accessToken: string
}> = ({
  onExit, open, enquiryId, accessToken
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enquiry, setEnquiry] = useState<EnquirySummary>();
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventSummary>();

  const { t } = useTranslation();

  useEffect(
    () => {
      setDialogOpen(open);
      if (enquiryId && open) {
        setLoading(true);
        enquirySummary(accessToken, enquiryId, t)
          .then(
            data => setEnquiry(data.data),
            error => setEnquiry(undefined)
          )
          .finally(() => setLoading(false));
      }
    },
    [ enquiryId, open ]
  );

  const exitFunction = () => {
    setDialogOpen(false);
    onExit();
  };

  const displayEventDetails = (details: EventSummary): void => {
    setSelectedEvent(details);
    setDialogOpen(false);
    setEventDetailsOpen(true);
  };

  const hideEventDetails = (): void => {
    setEventDetailsOpen(false);
    setDialogOpen(true);
  };

  const truncateData = (data: string): string => {
    if (data.length > 30) return data.substring(0, 27) + '...';
    return data;
  }

  const eventColumns: any[] = [
    {
      title: t('enquiry.summary.dialog.event.sequence'),
      dataIndex: 'version',
      className: 'right'
    },
    {
      title: t('enquiry.summary.dialog.event.type'),
      dataIndex: 'eventType',
    },
    {
      title: t('enquiry.summary.dialog.event.timestamp'),
      dataIndex: 'eventTransactionTS',
      render: (updated: Date, event: EventSummary) => updated?.toLocaleString()
    },
    {
      title: t('enquiry.summary.dialog.event.data'),
      render: (event: EventSummary) => <LinkButton
        title={t('enquiry.summary.dialog.event.display')}
        text={truncateData(event.data)}
        maxWidth="30"
        onClick={() => displayEventDetails(event)}
        />
    },
  ];

  return (
    <>
      <Modal
        title={t('enquiry.summary.dialog.title') + ": " + enquiryId}
        open={dialogOpen}
        className="enquiry-summary-dialog"
        footer={[
          <Button onClick={exitFunction} type="primary" key="ok">{t('ok.dialog.ok')}</Button>,
        ]}
        onCancel={exitFunction}
      >
        {loading
          ? (<div className="enquiry-summary-dialog-loading">{t('enquiry.summary.loading')}</div>)
          : (
            <div className="enquiry-summary-dialog-content">
              <h2>{t('enquiry.summary.dialog.enquiry.title')}: {enquiry?.enquiryId}</h2>
              <div className="row">
                <div className="column">
                  <table className="enquiry-summary-dialog-table">
                    <tbody>
                      <tr>
                        <th>{t('enquiry.summary.dialog.providerId')}</th>
                        <td>{enquiry?.providerId}</td>
                      </tr>
                      <tr>
                        <th>{t('retry.enquiry.dialog.applicationId')}</th>
                        <td>{enquiry?.applicationId}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.state')}</th>
                        <td>{enquiry?.state}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.branch')}</th>
                        <td>{enquiry?.branch}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.tag')}</th>
                        <td>{enquiry?.tag}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.name')}</th>
                        <td>{enquiry?.name}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="column">
                  <table className="enquiry-summary-dialog-table">
                    <tbody>
                    <tr>
                      <th>{t('enquiry.summary.dialog.address')}</th>
                      <td>{enquiry?.address}</td>
                    </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.phone')}</th>
                        <td>{enquiry?.phoneNumber}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.dob')}</th>
                        <td>{enquiry?.dob?.toLocaleDateString()}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.email')}</th>
                        <td>{enquiry?.email}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.advisor')}</th>
                        <td>{enquiry?.advisorEmail}</td>
                      </tr>
                      <tr>
                        <th>{t('enquiry.summary.dialog.created')}</th>
                        <td>{enquiry?.createdTimeStamp?.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              {(enquiry?.events && enquiry.events.length > 0) && <>
                  <h3>{t('enquiry.summary.dialog.history.title')}</h3>
                  <Table
                      className="event-table"
                      columns={eventColumns}
                      dataSource={enquiry.events}
                      rowKey="version"
                      pagination={false}
                  />
              </>}
              {(enquiry?.transactions && enquiry.transactions.length > 0) && <>
                <hr />
                <h2>{t('enquiry.summary.dialog.exams.title')}</h2>
                {
                  enquiry.transactions.map(transaction =>
                    <>
                      <h3>{t('enquiry.summary.dialog.exam.title')}: {transaction.transactionId}</h3>
                      <div className="row">
                        <div className="column">
                          <table className="enquiry-summary-dialog-table">
                            <tbody>
                              <tr>
                                <th>{t('enquiry.summary.dialog.providerId')}</th>
                                <td>{transaction?.providerId}</td>
                              </tr>
                              <tr>
                                <th>{t('enquiry.summary.dialog.medical.provider')}</th>
                                <td>{transaction?.medicalProviderId}</td>
                              </tr>
                              <tr>
                                <th>{t('enquiry.summary.dialog.provider.reference')}</th>
                                <td>{transaction?.providerReference}</td>
                              </tr>
                              <tr>
                                <th>{t('enquiry.summary.dialog.state')}</th>
                                <td>{transaction?.status}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="column">
                          <table className="enquiry-summary-dialog-table">
                            <tbody>
                              <tr>
                                <th>{t('enquiry.summary.dialog.evidence')}</th>
                                <td>{transaction?.evidenceRequested}</td>
                              </tr>
                              <tr>
                                <th>{t('enquiry.summary.dialog.updated')}</th>
                                <td>{transaction?.statusUpdated?.toLocaleString()}</td>
                              </tr>
                              <tr>
                                <th>{t('enquiry.summary.dialog.booking')}</th>
                                <td>{transaction?.bookingDateTime?.toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {(transaction?.events && transaction.events.length > 0) && <>
                        <h3>{t('enquiry.summary.dialog.history.title')}</h3>
                        <Table
                          className="event-table"
                          columns={eventColumns}
                          dataSource={transaction.events}
                          rowKey="version"
                          pagination={false}
                        />
                        <br />
                      </>}
                    </>
                  )
                }
              </>}
              {enquiry?.chainedEnquiry && <>
                <hr />
                <h2>{t('enquiry.summary.dialog.chained.title')}: {enquiry.chainedEnquiry.enquiryId}</h2>
                <table className="enquiry-summary-dialog-table">
                  <tbody>
                    <tr>
                      <th>{t('enquiry.summary.dialog.providerId')}</th>
                      <td>{enquiry?.providerId}</td>
                    </tr>
                    <tr>
                      <th>{t('enquiry.summary.dialog.state')}</th>
                      <td>{enquiry.chainedEnquiry.state}</td>
                    </tr>
                    <tr>
                      <th>{t('enquiry.summary.dialog.created')}</th>
                      <td>{enquiry.chainedEnquiry.createdTimeStamp?.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
                {(enquiry.chainedEnquiry?.events && enquiry.chainedEnquiry.events.length > 0) && <>
                  <h3>{t('enquiry.summary.dialog.history.title')}</h3>
                  <Table
                    className="event-table"
                    columns={eventColumns}
                    dataSource={enquiry.chainedEnquiry.events}
                    rowKey="version"
                    pagination={false}
                  />
                </>}
              </>}
            </div>
          )}
      </Modal>
      <EventDetailsDialog open={eventDetailsOpen} eventSummary={selectedEvent} onExit={hideEventDetails} />
      <div className="spinner">
        <Spin spinning={loading} />
      </div>
    </>
);
};

export default EnquirySummaryDialog;
